// ============================================================================
// CrewAhead — Institute API
// Aligned with backend: institute course offerings, batches, enquiries, and
// the institute profile endpoint used for approval-gated dashboard access.
// ============================================================================

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';
import type { InstituteProfile } from '../types/models';

// ── Shared types ────────────────────────────────────────────────────────────

export interface Paginated<T> {
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  results: T[];
}

export interface CourseMinimal {
  id: number;
  courseCode: string;
  courseName: string;
  category?: string | null;
}

export interface OfferingSummary {
  id: number;
  title: string;
  course: CourseMinimal;
  institute: { id: number; instituteName: string };
}

// ── Profile ───────────────────────────────────────────────────────────────────

export async function getMyInstituteProfile(): Promise<InstituteProfile> {
  try {
    const response = await apiClient.get<InstituteProfile>(
      API_ENDPOINTS.INSTITUTE.PROFILE_ME
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export interface InstituteProfileUpdate {
  instituteName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  cityCountryId?: number | null;
  country?: string;
  countryCountryId?: number | null;
  postalCode?: string;
  website?: string;
  email?: string;
  phone?: string;
  registrationNumber?: string;
}

export async function updateMyInstituteProfile(
  data: InstituteProfileUpdate
): Promise<InstituteProfile> {
  try {
    const response = await apiClient.patch<InstituteProfile>(
      API_ENDPOINTS.INSTITUTE.PROFILE_UPDATE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ── Offerings ───────────────────────────────────────────────────────────────

export interface InstituteCourseOffering {
  id: number;
  institute: { id: number; instituteName: string };
  course: CourseMinimal;
  courseId: number;
  title: string;
  description: string;
  deliveryMode: string;
  durationWeeks: number | null;
  fee: string | null;
  currency: string;
  status: 'draft' | 'published' | 'closed';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicInstituteCourseOffering {
  id: number;
  institute: { id: number; instituteName: string };
  course: CourseMinimal;
  title: string;
  description: string;
  deliveryMode: string;
  durationWeeks: number | null;
  fee: string | null;
  currency: string;
  status: 'draft' | 'published' | 'closed';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InstituteCourseOfferingInput {
  courseId: number;
  title?: string;
  description?: string;
  deliveryMode?: string;
  durationWeeks?: number | null;
  fee?: string | null;
  currency?: string;
  status?: 'draft' | 'published' | 'closed';
  isActive?: boolean;
}

export async function getOfferings(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<Paginated<InstituteCourseOffering>> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));
    if (params?.search) query.set('search', params.search);

    const url = `${API_ENDPOINTS.INSTITUTE.OFFERINGS}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<InstituteCourseOffering>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getOffering(id: string | number): Promise<InstituteCourseOffering> {
  try {
    const response = await apiClient.get<InstituteCourseOffering>(
      API_ENDPOINTS.INSTITUTE.OFFERING_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function createOffering(
  data: InstituteCourseOfferingInput
): Promise<InstituteCourseOffering> {
  try {
    const response = await apiClient.post<InstituteCourseOffering>(
      API_ENDPOINTS.INSTITUTE.OFFERINGS,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function updateOffering(
  id: string | number,
  data: Partial<InstituteCourseOfferingInput>
): Promise<InstituteCourseOffering> {
  try {
    const response = await apiClient.patch<InstituteCourseOffering>(
      API_ENDPOINTS.INSTITUTE.OFFERING_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function deleteOffering(id: string | number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.INSTITUTE.OFFERING_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ── Batches ───────────────────────────────────────────────────────────────────

export interface CourseBatch {
  id: number;
  offering: OfferingSummary;
  offeringId: number;
  deliveryMode: string;
  startDate: string | null;
  endDate: string | null;
  registrationDeadline: string | null;
  totalSeats: number | null;
  enrolledCount: number;
  seatsRemaining: number | null;
  fee: string | null;
  currency: string;
  status: 'draft' | 'open_for_enrolment' | 'in_progress' | 'completed' | 'cancelled';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicCourseBatch {
  id: number;
  offering: OfferingSummary;
  deliveryMode: string;
  startDate: string | null;
  endDate: string | null;
  registrationDeadline: string | null;
  totalSeats: number | null;
  enrolledCount: number;
  seatsRemaining: number | null;
  fee: string | null;
  currency: string;
  status: 'draft' | 'open_for_enrolment' | 'in_progress' | 'completed' | 'cancelled';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CourseBatchInput {
  offeringId: number;
  deliveryMode?: string;
  startDate?: string | null;
  endDate?: string | null;
  registrationDeadline?: string | null;
  totalSeats?: number | null;
  enrolledCount?: number;
  fee?: string | null;
  currency?: string;
  status?: 'draft' | 'open_for_enrolment' | 'in_progress' | 'completed' | 'cancelled';
  isActive?: boolean;
}

export async function getBatches(params?: {
  offeringId?: number;
  page?: number;
  pageSize?: number;
}): Promise<Paginated<CourseBatch>> {
  try {
    const query = new URLSearchParams();
    if (params?.offeringId !== undefined) query.set('offering_id', String(params.offeringId));
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const url = `${API_ENDPOINTS.INSTITUTE.BATCHES}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<CourseBatch>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getBatch(id: string | number): Promise<CourseBatch> {
  try {
    const response = await apiClient.get<CourseBatch>(
      API_ENDPOINTS.INSTITUTE.BATCH_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function createBatch(data: CourseBatchInput): Promise<CourseBatch> {
  try {
    const response = await apiClient.post<CourseBatch>(
      API_ENDPOINTS.INSTITUTE.BATCHES,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function updateBatch(
  id: string | number,
  data: Partial<CourseBatchInput>
): Promise<CourseBatch> {
  try {
    const response = await apiClient.patch<CourseBatch>(
      API_ENDPOINTS.INSTITUTE.BATCH_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function deleteBatch(id: string | number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.INSTITUTE.BATCH_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ── Public discovery ──────────────────────────────────────────────────────────

export interface PublicInstitute {
  id: number;
  instituteName: string;
  city: string;
  country: string;
  cityCountry: string;
  countryCountry: string;
  courseOfferingCount: number;
}

export async function getPublicInstitutes(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  city?: string;
  country?: string;
}): Promise<Paginated<PublicInstitute>> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));
    if (params?.search) query.set('search', params.search);
    if (params?.city) query.set('city', params.city);
    if (params?.country) query.set('country', params.country);

    const url = `${API_ENDPOINTS.INSTITUTE.PUBLIC_INSTITUTES}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<PublicInstitute>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getPublicOfferings(params?: {
  page?: number;
  pageSize?: number;
  excludeInstitute?: number;
}): Promise<Paginated<PublicInstituteCourseOffering>> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));
    if (params?.excludeInstitute !== undefined) query.set('exclude_institute', String(params.excludeInstitute));

    const url = `${API_ENDPOINTS.INSTITUTE.PUBLIC_OFFERINGS}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<PublicInstituteCourseOffering>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getPublicOfferingDetail(id: string | number): Promise<PublicInstituteCourseOffering> {
  try {
    const response = await apiClient.get<PublicInstituteCourseOffering>(
      API_ENDPOINTS.INSTITUTE.PUBLIC_OFFERING_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getPublicBatches(params?: {
  offeringId?: number;
  page?: number;
  pageSize?: number;
}): Promise<Paginated<PublicCourseBatch>> {
  try {
    const query = new URLSearchParams();
    if (params?.offeringId !== undefined) query.set('offering_id', String(params.offeringId));
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const url = `${API_ENDPOINTS.INSTITUTE.PUBLIC_BATCHES}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<PublicCourseBatch>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ── Enquiries ─────────────────────────────────────────────────────────────────

export interface CourseBatchSummary {
  id: number;
  startDate: string | null;
  endDate: string | null;
  status: string;
}

export interface EnquirerSummary {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface CourseEnquiry {
  id: number;
  offering: OfferingSummary;
  courseBatch: CourseBatchSummary | null;
  user: number;
  submitter: EnquirerSummary;
  message: string;
  status: 'new' | 'in_progress' | 'resolved' | 'spam';
  createdAt: string;
  updatedAt: string;
}

export interface CourseEnquiryInput {
  offeringId: number;
  courseBatchId?: number | null;
  message: string;
}

export async function createEnquiry(data: CourseEnquiryInput): Promise<CourseEnquiry> {
  try {
    const response = await apiClient.post<CourseEnquiry>(
      API_ENDPOINTS.INSTITUTE.ENQUIRIES,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getEnquiries(params?: {
  page?: number;
  pageSize?: number;
  status?: CourseEnquiry['status'];
}): Promise<Paginated<CourseEnquiry>> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));
    if (params?.status) query.set('status', params.status);

    const url = `${API_ENDPOINTS.INSTITUTE.ENQUIRIES}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<CourseEnquiry>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getEnquiryDetail(id: string | number): Promise<CourseEnquiry> {
  try {
    const response = await apiClient.get<CourseEnquiry>(
      API_ENDPOINTS.INSTITUTE.ENQUIRY_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function updateEnquiryStatus(
  id: string | number,
  status: CourseEnquiry['status']
): Promise<CourseEnquiry> {
  try {
    const response = await apiClient.patch<CourseEnquiry>(
      API_ENDPOINTS.INSTITUTE.ENQUIRY_DETAIL(id),
      { status }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ── Resources ─────────────────────────────────────────────────────────────────

export interface InstituteResource {
  id: number;
  institute: { id: number; instituteName: string };
  category: string;
  name: string;
  description: string;
  capacity: number | null;
  status: 'draft' | 'published' | 'unavailable';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicInstituteResource {
  id: number;
  institute: { id: number; instituteName: string };
  category: string;
  name: string;
  description: string;
  capacity: number | null;
  status: 'draft' | 'published' | 'unavailable';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InstituteResourceInput {
  category: string;
  name: string;
  description?: string;
  capacity?: number | null;
  status?: 'draft' | 'published' | 'unavailable';
  isActive?: boolean;
}

export async function getResources(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<Paginated<InstituteResource>> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));
    if (params?.search) query.set('search', params.search);

    const url = `${API_ENDPOINTS.INSTITUTE.RESOURCES}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<InstituteResource>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getResource(id: string | number): Promise<InstituteResource> {
  try {
    const response = await apiClient.get<InstituteResource>(
      API_ENDPOINTS.INSTITUTE.RESOURCE_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function createResource(
  data: InstituteResourceInput
): Promise<InstituteResource> {
  try {
    const response = await apiClient.post<InstituteResource>(
      API_ENDPOINTS.INSTITUTE.RESOURCES,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function updateResource(
  id: string | number,
  data: Partial<InstituteResourceInput>
): Promise<InstituteResource> {
  try {
    const response = await apiClient.patch<InstituteResource>(
      API_ENDPOINTS.INSTITUTE.RESOURCE_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function deleteResource(id: string | number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.INSTITUTE.RESOURCE_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ── Public resource discovery ─────────────────────────────────────────────────

export async function getPublicResources(params?: {
  page?: number;
  pageSize?: number;
  excludeInstitute?: number;
}): Promise<Paginated<PublicInstituteResource>> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));
    if (params?.excludeInstitute !== undefined) query.set('exclude_institute', String(params.excludeInstitute));

    const url = `${API_ENDPOINTS.INSTITUTE.PUBLIC_RESOURCES}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<PublicInstituteResource>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getPublicResourceDetail(id: string | number): Promise<PublicInstituteResource> {
  try {
    const response = await apiClient.get<PublicInstituteResource>(
      API_ENDPOINTS.INSTITUTE.PUBLIC_RESOURCE_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ── Resource Enquiries ────────────────────────────────────────────────────────

export interface ResourceEnquiry {
  id: number;
  resource: {
    id: number;
    name: string;
    category: string;
    institute: { id: number; instituteName: string };
  };
  user: number;
  submitter: EnquirerSummary;
  message: string;
  status: 'new' | 'in_progress' | 'resolved' | 'spam';
  createdAt: string;
  updatedAt: string;
}

export interface ResourceEnquiryInput {
  resourceId: number;
  message: string;
}

export async function createResourceEnquiry(data: ResourceEnquiryInput): Promise<ResourceEnquiry> {
  try {
    const response = await apiClient.post<ResourceEnquiry>(
      API_ENDPOINTS.INSTITUTE.RESOURCE_ENQUIRIES,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getResourceEnquiries(params?: {
  page?: number;
  pageSize?: number;
  status?: ResourceEnquiry['status'];
}): Promise<Paginated<ResourceEnquiry>> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));
    if (params?.status) query.set('status', params.status);

    const url = `${API_ENDPOINTS.INSTITUTE.RESOURCE_ENQUIRIES}${query.toString() ? `?${query.toString()}` : ''}`;
    const response = await apiClient.get<Paginated<ResourceEnquiry>>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getResourceEnquiryDetail(id: string | number): Promise<ResourceEnquiry> {
  try {
    const response = await apiClient.get<ResourceEnquiry>(
      API_ENDPOINTS.INSTITUTE.RESOURCE_ENQUIRY_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function updateResourceEnquiryStatus(
  id: string | number,
  status: ResourceEnquiry['status']
): Promise<ResourceEnquiry> {
  try {
    const response = await apiClient.patch<ResourceEnquiry>(
      API_ENDPOINTS.INSTITUTE.RESOURCE_ENQUIRY_DETAIL(id),
      { status }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
